import React, { useState, useEffect, useRef, useMemo } from 'react';

/**
 * iOS-style drum picker for weight values (integer + decimal columns).
 * Internally works in kg but renders kg/lbs based on `unit`.
 * Extracted from NuovoPiano.js so multiple screens can reuse it.
 */
export default function WeightDrumPicker({ value, onChange, isDark, unit = 'kg' }) {
  const isLbs = unit === 'lbs';
  const minVal = isLbs ? 44 : 20;
  const maxVal = isLbs ? 440 : 200;
  const range = useMemo(
    () => Array.from({ length: maxVal - minVal + 1 }, (_, i) => i + minVal),
    [minVal, maxVal],
  );
  const decimalRange = useMemo(() => Array.from({ length: 10 }, (_, i) => i), []);

  const displayValue = value || (isLbs ? 198 : 90);
  const convertedValue = isLbs ? displayValue * 2.20462 : displayValue;
  const currentMain = Math.floor(convertedValue);
  const currentDecimal = Math.round((convertedValue - Math.floor(convertedValue)) * 10);

  const mainRef = useRef(null);
  const decimalRef = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize scroll position on mount (and whenever `value` changes
  // externally, e.g. when the parent picks a different plan).
  useEffect(() => {
    const initValue = value || (isLbs ? 90 : 90);
    const converted = isLbs ? initValue * 2.20462 : initValue;
    const main = Math.floor(converted);
    const decimal = Math.round((converted - Math.floor(converted)) * 10);

    if (mainRef.current) {
      mainRef.current.style.scrollBehavior = 'auto';
      const index = range.indexOf(main);
      if (index !== -1) mainRef.current.scrollTop = index * 44;
      setTimeout(() => {
        if (mainRef.current) mainRef.current.style.scrollBehavior = 'smooth';
      }, 100);
    }
    if (decimalRef.current) {
      decimalRef.current.style.scrollBehavior = 'auto';
      decimalRef.current.scrollTop = decimal * 44;
      setTimeout(() => {
        if (decimalRef.current) decimalRef.current.style.scrollBehavior = 'smooth';
      }, 100);
    }
    setTimeout(() => setIsInitialized(true), 200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMainScroll = (e) => {
    if (!isInitialized) return;
    const scrollTop = e.target.scrollTop;
    const index = Math.round(scrollTop / 44);
    if (range[index] !== undefined) {
      const displayVal = range[index] + (currentDecimal / 10);
      const kgValue = isLbs ? displayVal / 2.20462 : displayVal;
      onChange(kgValue);
    }
  };

  const handleDecimalScroll = (e) => {
    if (!isInitialized) return;
    const scrollTop = e.target.scrollTop;
    const index = Math.round(scrollTop / 44);
    if (decimalRange[index] !== undefined) {
      const displayVal = currentMain + (decimalRange[index] / 10);
      const kgValue = isLbs ? displayVal / 2.20462 : displayVal;
      onChange(kgValue);
    }
  };

  return (
    <div>
      <div className="text-center mb-2">
        <span className={`font-display text-4xl font-bold ${isDark ? 'text-white' : 'text-[#1d3557]'}`}>
          {currentMain}.{currentDecimal}
        </span>
        <span className={`text-lg ${isDark ? 'text-white/50' : 'text-gray-500'}`}> {unit}</span>
      </div>

      <div className="flex justify-center gap-16 text-[10px] font-medium uppercase tracking-wider mb-1">
        <span className={isDark ? 'text-white/50' : 'text-gray-500'}>{isLbs ? 'LBS' : 'KG'}</span>
        <span className={isDark ? 'text-white/50' : 'text-gray-500'}>.{isLbs ? 'LBS' : 'G'}</span>
      </div>

      <div className={`flex items-center justify-center rounded-xl overflow-hidden h-36 ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
        <div className="relative h-full w-24 overflow-hidden">
          <div
            ref={mainRef}
            className="h-full overflow-y-scroll no-scrollbar snap-y snap-mandatory"
            style={{ scrollBehavior: 'smooth' }}
            onScroll={handleMainScroll}
          >
            <div className="h-[44px]"></div>
            {range.map(val => (
              <div
                key={val}
                className={`h-11 flex items-center justify-center snap-center font-display transition-all ${
                  currentMain === val
                    ? (isDark ? 'text-white font-bold text-3xl' : 'text-[#1d3557] font-bold text-3xl')
                    : Math.abs(currentMain - val) === 1
                      ? (isDark ? 'text-white/40 text-xl' : 'text-gray-400 text-xl')
                      : (isDark ? 'text-white/20 text-lg' : 'text-gray-300 text-lg')
                }`}
              >
                {val}
              </div>
            ))}
            <div className="h-[44px]"></div>
          </div>
          <div
            className="absolute inset-x-3 top-1/2 -translate-y-1/2 h-11 border-y pointer-events-none"
            style={{ borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}
          />
        </div>

        <span className={`font-display text-3xl font-bold ${isDark ? 'text-white/30' : 'text-gray-300'}`}>.</span>

        <div className="relative h-full w-16 overflow-hidden">
          <div
            ref={decimalRef}
            className="h-full overflow-y-scroll no-scrollbar snap-y snap-mandatory"
            style={{ scrollBehavior: 'smooth' }}
            onScroll={handleDecimalScroll}
          >
            <div className="h-[44px]"></div>
            {decimalRange.map(d => (
              <div
                key={d}
                className={`h-11 flex items-center justify-center snap-center font-display transition-all ${
                  currentDecimal === d
                    ? (isDark ? 'text-white font-bold text-3xl' : 'text-[#1d3557] font-bold text-3xl')
                    : Math.abs(currentDecimal - d) === 1
                      ? (isDark ? 'text-white/40 text-xl' : 'text-gray-400 text-xl')
                      : (isDark ? 'text-white/20 text-lg' : 'text-gray-300 text-lg')
                }`}
              >
                {d}
              </div>
            ))}
            <div className="h-[44px]"></div>
          </div>
          <div
            className="absolute inset-x-3 top-1/2 -translate-y-1/2 h-11 border-y pointer-events-none"
            style={{ borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}
          />
        </div>
      </div>
    </div>
  );
}
